import React, { useState, useEffect, useRef } from "react";
import * as Plotly from 'plotly.js-dist';

interface Props {
    geneName: string;
    rsIDs?: string[]; // Changed from rsID to rsIDs array
    showTable?: boolean; // Show region-specific frequency table below the map
    rows?: any[]; // Pre-parsed gnomAD rows shared from the page (skips own fetch)
}

const regionToCountries = {
  'AFR': ['South Africa', 'Nigeria', 'Kenya'],
  'AMR': ['United States of America', 'Brazil', 'Mexico'], 
  'WPR': ['China', 'Japan', 'Australia'],
  'EUR': ['Germany', 'United Kingdom', 'France'],
  'EMR': ['Egypt', 'Saudi Arabia', 'Iran'],
  'SEAR': ['India', 'Sri Lanka', 'Bangladesh']
};

function transformData(data: any[]) {
  const expanded: any[] = [];
  data.forEach(row => {
    const countries = regionToCountries[row.WHO_Region as keyof typeof regionToCountries] || [];
    countries.forEach(country => {
      expanded.push({
        ...row,
        Country: country
      });
    });
  });
  return expanded;
}

function FrequencyMap({geneName, rsIDs, showTable = false, rows} : Props){
    const [mapData, setMapData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [noMatch, setNoMatch] = useState<string | null>(null);
    const mapRef = useRef<HTMLDivElement>(null);
    const [rawData, setRawData] = useState<any[]>([]);
    const [populationTableData, setPopulationTableData] = useState<{population: string; whoRegion: string; frequency: number}[]>([]);

    useEffect(() => {
        // Prefer pre-parsed rows shared from the page (avoids a duplicate
        // 5MB fetch and the map loading after the page has rendered).
        if (rows) {
            setRawData(rows);
            setMapData(transformData(rows));
            setLoading(false);
            return;
        }

        // Fetch this gene's frequency rows from the DB (replaces the 5MB CSV).
        const loadMapData = async () => {
            if (!geneName) { setLoading(false); return; }
            setLoading(true);
            setError(null);
            try {
                const response = await fetch(`/api/getData?type=frequency&gene=${encodeURIComponent(geneName)}`);
                if (!response.ok) {
                    throw new Error(`Failed to fetch frequency data: ${response.status}`);
                }
                const json = await response.json();
                const data: any[] = Array.isArray(json.rows) ? json.rows : [];
                setRawData(data);
                setMapData(transformData(data));
                setLoading(false);
            } catch (err) {
                console.error('Error loading map data:', err);
                setError('Failed to load frequency data');
                setLoading(false);
            }
        };

        loadMapData();
    }, [rows, geneName]); // Use shared rows if provided; else fetch by gene

    useEffect(() => {
        if (!loading && mapData.length > 0 && mapRef.current && geneName) {
            setNoMatch(null);

            // Filter data based on gene and optionally multiple rsIDs (matching server.js logic)
            let filteredData;
            
            if (rsIDs && rsIDs.length > 0) {
                // Filter by multiple rsIDs (which implicitly includes the gene relationship)
                filteredData = mapData.filter(row => 
                    rsIDs.includes(row.rsID) && row.Gene_Name == geneName
                );
            } else {
                // Filter by gene only
                filteredData = mapData.filter(row => row.Gene_Name === geneName);
            }
            
            // Group by country and average frequencies (matching server.js logic)
            const countryFreq: {[key: string]: number[]} = {};
            filteredData.forEach(row => {
                if (!countryFreq[row.Country]) {
                    countryFreq[row.Country] = [];
                }
                countryFreq[row.Country].push(row.Frequency);
            });

            // Calculate average frequency per country
            const countries = Object.keys(countryFreq);
            const frequencies = countries.map(country => 
                countryFreq[country].reduce((a, b) => a + b, 0) / countryFreq[country].length
            );

            // Only plot if we have data. This isn't an error condition — gnomAD
            // simply has no population frequency rows for these RSIDs (common for
            // rare/clinical variants) — so it's shown as a neutral empty state.
            if (countries.length === 0) {
                const rsidText = rsIDs && rsIDs.length > 0
                    ? (rsIDs.length > 1 ? `these ${rsIDs.length} RSIDs` : `RSID ${rsIDs[0]}`)
                    : `gene ${geneName}`;
                setNoMatch(`No population frequency data available for ${rsidText}`);
                return;
            }

            const plotData = [{
                type: 'choropleth' as const,
                locations: countries,
                z: frequencies,
                locationmode: 'country names' as const,
                colorscale: [
                    [0, '#e8f4f8'],      // Very light blue
                    [0.2, '#a8d5e2'],    // Light blue
                    [0.4, '#5fa8c5'],    // Medium blue
                    [0.6, '#3d7fa8'],    // Dark blue
                    [0.8, '#2a5a7a'],    // Darker blue
                    [1, '#1A2940']       // Website theme color
                ] as any,
                hovertemplate: '<b>%{location}</b><br>Frequency: %{z:.2e}<extra></extra>',
                colorbar: {
                    title: {
                        text: "Frequency",
                        font: { color: '#1A2940' }
                    },
                    tickformat: ".2e",
                    tickfont: { color: '#1A2940' },
                    len: 0.5,
                    thickness: 15,
                    x: 1.02,
                    xanchor: 'left' as const
                }
            }];

            const title = rsIDs && rsIDs.length > 0 
                ? `Allele Frequency for ${rsIDs.length > 1 ? 'Multiple RSIDs' : 'RSID'}: ${rsIDs.join(', ')}`
                : `Allele Frequency for Gene: ${geneName}`;

            const layout = {
                title: {
                    text: title,
                    font: { color: '#1A2940', size: 16 }
                },
                geo: {
                    showframe: false,
                    showcoastlines: true,
                    coastlinecolor: '#1A2940',
                    projection: { type: 'natural earth' as const },
                    showcountries: true,
                    countrycolor: '#1A2940'
                },
                autosize: true,
                margin: { t: 0, b: 0, l: 0, r: 0 },
                showlegend: false,
                paper_bgcolor: 'rgba(0,0,0,0)',
                plot_bgcolor: 'rgba(0,0,0,0)'
            };

            const config = {
                responsive: true,
                displayModeBar: false
            };

            Plotly.newPlot(mapRef.current, plotData, layout, config);

            // Compute per-population data for table from raw (pre-transform) data
            if (showTable && rawData.length > 0) {
                let rawFiltered;
                if (rsIDs && rsIDs.length > 0) {
                    rawFiltered = rawData.filter(row =>
                        rsIDs.includes(row.rsID) && row.Gene_Name == geneName
                    );
                } else {
                    rawFiltered = rawData.filter(row => row.Gene_Name === geneName);
                }

                // Group by population and average frequencies
                const popFreq: {[key: string]: {whoRegion: string; freqs: number[]}} = {};
                rawFiltered.forEach((row: any) => {
                    const pop = row.Population || 'Unknown';
                    if (!popFreq[pop]) {
                        popFreq[pop] = { whoRegion: row.WHO_Region || '', freqs: [] };
                    }
                    popFreq[pop].freqs.push(row.Frequency);
                });

                const popData = Object.entries(popFreq).map(([population, data]) => ({
                    population,
                    whoRegion: data.whoRegion,
                    frequency: data.freqs.reduce((a, b) => a + b, 0) / data.freqs.length
                }));
                popData.sort((a, b) => b.frequency - a.frequency);
                setPopulationTableData(popData);
            }
        }
    }, [mapData, rawData, geneName, rsIDs, loading, showTable]);

    // Plotly's responsive config only reacts to window resizes. A
    // ResizeObserver keeps the choropleth fitted to its container when the
    // surrounding layout changes (e.g. the structure panel expanding).
    useEffect(() => {
        const el = mapRef.current;
        if (!el) return;
        const observer = new ResizeObserver(() => {
            // Skip resizing when the plot isn't actually displayed (e.g. it lives
            // on a hidden tab). Plotly.Plots.resize rejects for hidden/zero-size
            // divs, and that rejection is a Promise that a try/catch can't catch.
            if (!el.offsetParent || el.offsetWidth === 0 || el.offsetHeight === 0) return;
            try {
                const result = (Plotly as any).Plots.resize(el);
                if (result && typeof result.catch === 'function') {
                    result.catch(() => { /* div hidden/removed mid-resize */ });
                }
            } catch (e) { /* not yet plotted */ }
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [loading]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-600">Loading map...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-red-600">Error: {error}</div>
            </div>
        );
    }

    if (noMatch) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-600">{noMatch}</div>
            </div>
        );
    }

    if (mapData.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-600">No data available</div>
            </div>
        );
    }

    return (
        <div className="w-full">
            <div ref={mapRef} style={{ width: '100%', height: '300px' }}></div>
            {showTable && populationTableData.length > 0 && (
                <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0" style={{ color: '#1A2940', fontWeight: 600 }}>Population-Specific Allele Frequencies</h6>
                        <button
                            className="btn btn-sm"
                            style={{ backgroundColor: '#1A2940', color: 'white' }}
                            onClick={() => {
                                const header = 'WHO Region,Population,Allele Frequency\n';
                                const sorted = [...populationTableData].sort((a, b) => a.whoRegion.localeCompare(b.whoRegion) || b.frequency - a.frequency);
                                const rows = sorted.map(r =>
                                    `"${r.whoRegion}","${r.population}",${r.frequency.toExponential(4)}`
                                ).join('\n');
                                const blob = new Blob([header + rows], { type: 'text/csv' });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                const rsidLabel = rsIDs && rsIDs.length > 0 ? rsIDs.join('_') : geneName;
                                a.download = `allele_frequencies_${rsidLabel}.csv`;
                                a.click();
                                URL.revokeObjectURL(url);
                            }}
                        >
                            Download CSV
                        </button>
                    </div>
                    {(() => {
                        // Group by WHO Region
                        const regionOrder = ['AFR', 'AMR', 'EMR', 'EUR', 'SEAR', 'WPR', 'Other'];
                        const regionLabels: Record<string, string> = {
                            'AFR': 'Africa (AFR)',
                            'AMR': 'Americas (AMR)',
                            'EMR': 'Eastern Mediterranean (EMR)',
                            'EUR': 'Europe (EUR)',
                            'SEAR': 'South-East Asia (SEAR)',
                            'WPR': 'Western Pacific (WPR)',
                            'Other': 'Other',
                        };
                        const grouped: Record<string, typeof populationTableData> = {};
                        populationTableData.forEach(row => {
                            const region = row.whoRegion || 'Other';
                            if (!grouped[region]) grouped[region] = [];
                            grouped[region].push(row);
                        });
                        // Sort regions by defined order, then sort populations within each by frequency desc
                        const sortedRegions = Object.keys(grouped).sort((a, b) =>
                            (regionOrder.indexOf(a) === -1 ? 99 : regionOrder.indexOf(a)) -
                            (regionOrder.indexOf(b) === -1 ? 99 : regionOrder.indexOf(b))
                        );
                        sortedRegions.forEach(r => grouped[r].sort((a, b) => b.frequency - a.frequency));

                        return (
                            <table className="table table-bordered table-sm mb-0" style={{ fontSize: '0.9rem' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#1A2940', color: 'white' }}>
                                        <th>Population</th>
                                        <th>Allele Frequency</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedRegions.map(region => (
                                        <React.Fragment key={region}>
                                            <tr>
                                                <td colSpan={2} style={{
                                                    backgroundColor: '#f0f4f8',
                                                    fontWeight: 700,
                                                    fontSize: '0.8rem',
                                                    color: '#1A2940',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.04em',
                                                    padding: '0.4rem 0.5rem',
                                                    borderBottom: '2px solid #60a5fa',
                                                }}>
                                                    {regionLabels[region] || region}
                                                </td>
                                            </tr>
                                            {grouped[region].map((row, idx) => (
                                                <tr key={`${region}-${idx}`}>
                                                    <td style={{ paddingLeft: '1.25rem' }}>{row.population}</td>
                                                    <td style={{ fontFamily: 'monospace' }}>{row.frequency.toExponential(4)}</td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        );
                    })()}
                </div>
            )}
        </div>
    );
} 

export default FrequencyMap;